import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchArgs } from '@reduxjs/toolkit/query';

import {
  deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId,
  patchFieldingGroupsByFieldingGroupIdStepsAndStepId,
  patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals,
  patchProjectsByProjectIdFieldingGroupAndFieldingGroupId,
  patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus,
  patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds,
  putProjectsByProjectIdAudienceBuilderAudience,
  putSurveyconfigsBySurveyConfigIdShowlogicsAndId,
} from './optimistic-updates/endpoints';
import {
  CHAT_CONVERSATIONS_LIST_TAG,
  PERSONA_CHAT_CONVERSATIONS_LIST_TAG,
  RESULTS_INVALIDATION_TAGS,
  TAG_TYPES,
} from './rtk/constants';
import type { components } from './fetch/fetch.generated';
import type * as Types from './rtk/rtk.generated';
import type {
  ConfirmedDelivery,
  DeliveryConfirmationRequest,
  SavedDeliveryReceipt,
  SavedShipment,
} from './shipments';
import type { RecruitCountRequest } from './recruit-count';
import type { Organization, Product, ProjectSummaryResponse } from './rtk/rtk.generated';
import { projectApi as _projectApi } from './rtk/rtk.generated';
import type {
  ComputeFeasibilityEstimateApiArg,
  ComputeFeasibilityEstimateApiResponse,
} from './rtk/types';

type FetchSchemas = components['schemas'];

export * from './rtk/types';
export * from './mocks';
export * from './constants';

/**
 *  @deprecated prefer direct imports from `@highlight-cp/project-api/types`
 */
export type { Types as ProjectApiTypes };

export type {
  CreateCustomTopicRequest,
  CustomTopic,
  GetProjectsByProjectIdRecommendationApiArg,
  GetProjectsByProjectIdRecommendationApiResponse,
  PostProjectsByProjectIdRecommendationApiArg,
  PostProjectsByProjectIdRecommendationApiResponse,
  ProjectIndexLegendItem,
  ProjectRecommendationResponse,
  RankedLineItemResponse,
  RecommendationGenerationStatus,
  StepBase,
  StepBase as Step,
  SubscriptionBalance,
} from './rtk/rtk.generated';

export type CustomerRegistrationRequest = FetchSchemas['CustomerRegistrationRequest'];

export * from './rtk/constants';
export * from './rtk/survey-roles';
export {
  FIELDING_STARTED_STATUSES,
  isFieldingStarted,
  isPreFielding,
  isProjectStatusAllowedForImportCriteria,
  isProjectStatusAllowedForRecruitingStatus,
  STATUSES_ALLOWED_FOR_IMPORT_CRITERIA,
  STATUSES_ALLOWED_FOR_RECRUITING,
} from './rtk/utils';

/**
 *  @deprecated prefer direct imports from `@highlight-cp/project-api/types`
 */
export * from './types';

export * from './kit-box-dimensions';

// Module types constant (previously in modules.ts)
export const MODULE_TYPES = {
  DEFAULT: 'default',
  RECOMMENDED: 'recommended',
} as const;

/**
 * Lifecycle states for a custom topic's classification (mirrors survey-api's
 * `CustomTopicStatus`). A topic is `classifying` until its backfill finishes,
 * then `ready` (assignments queryable) or `failed`.
 */
export const CUSTOM_TOPIC_STATUS = {
  CLASSIFYING: 'classifying',
  READY: 'ready',
  FAILED: 'failed',
} as const;

// Construct the enhanced API instance
export const projectApi = _projectApi
  .enhanceEndpoints({
    endpoints: {
      // Admins enhancer (inlined)
      getAdmins: {
        providesTags: (admins) =>
          admins
            ? [
                ...admins.map(({ id }) => ({
                  type: TAG_TYPES.ADMIN,
                  id,
                })),
                TAG_TYPES.ADMIN,
              ]
            : [TAG_TYPES.ADMIN],
      },

      getProjectsByProjectIdAdmins: {
        providesTags: (admins) =>
          admins
            ? [
                ...admins.map(({ id }) => ({
                  type: TAG_TYPES.ADMIN,
                  id,
                })),
                TAG_TYPES.ADMIN,
              ]
            : [TAG_TYPES.ADMIN],
      },

      // Attachments enhancer (inlined)
      getProjectsByProjectIdAttachments: {
        providesTags: (projectAttachments) =>
          projectAttachments?.length
            ? projectAttachments.map(({ projectAttachmentId }) => ({
                type: TAG_TYPES.PROJECT_ATTACHMENT,
                id: projectAttachmentId ?? undefined,
              }))
            : [TAG_TYPES.PROJECT_ATTACHMENT],
      },

      putProjectsByProjectIdAttachmentsAndAttachmentId: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PROJECT_ATTACHMENT,
            id: arg.projectAttachment.projectAttachmentId ?? undefined,
          },
        ],
      },

      // Audiences enhancer (inlined)
      // Nothing invalidates `AudienceData` any more — the PUT below fills this entry from its own
      // response instead (EPD-1418) — but the tag is not dead weight: it is what keeps the audience
      // document off the generated `Audience` tag, which `postProjectsByProjectIdAudienceAttachments`
      // invalidates. Drop it and every autosave starts refetching attachments.
      getProjectsByProjectIdAudienceBuilderAudience: {
        providesTags: () => [TAG_TYPES.AUDIENCE_DATA],
      },
      putProjectsByProjectIdAudienceBuilderAudience:
        putProjectsByProjectIdAudienceBuilderAudience(_projectApi),

      // ChatConversations enhancer (inlined)
      // The list holds a sentinel `id: 'LIST'` identity of its own, and callers that only want the
      // list refreshed invalidate *that* rather than the bare type. This matters: RTK resolves an
      // id-less tag through `Object.values(provided).flat()`, so `{ type: 'ChatConversations' }`
      // matches every id under the type — including whichever conversation's detail entry is open,
      // whose transcript is the largest payload in the feature. Do not "simplify" this back to the
      // bare tag. Per-item id tags are kept alongside so a rename or delete can target one row.
      getProjectsByProjectIdChatConversations: {
        providesTags: (conversations) => [
          CHAT_CONVERSATIONS_LIST_TAG,
          ...(conversations ?? []).map(({ id }) => ({
            type: 'ChatConversations' as const,
            id,
          })),
        ],
      },
      getProjectsByProjectIdChatConversationsAndConversationId: {
        providesTags: (_result, _error, arg) => [
          { type: 'ChatConversations' as const, id: arg.conversationId },
        ],
      },
      // The append mutation has no browser caller — messages go browser → Next route →
      // ProjectApi's fetch client, never through Redux — so this generated `invalidatesTags`
      // would never fire anyway. Left invalidating nothing rather than implying a dead endpoint
      // keeps the list current; `useInsightsChatConversation`'s `onFinish` does that instead.
      patchProjectsByProjectIdChatConversationsAndConversationId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'ChatConversations' as const, id: arg.conversationId },
          CHAT_CONVERSATIONS_LIST_TAG,
        ],
      },
      // Delete invalidates the removed conversation's id tag and the list sentinel; the id tag is a
      // no-op once the row is gone, but keeps this pair symmetric with the rename above.
      deleteProjectsByProjectIdChatConversationsAndConversationId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'ChatConversations' as const, id: arg.conversationId },
          CHAT_CONVERSATIONS_LIST_TAG,
        ],
      },

      // PersonaChatConversations enhancer — same LIST-sentinel pattern as Insights ChatConversations.
      getOrganizationsByOrgIdPersonaPanelsAndPersonaPanelIdChatConversations: {
        providesTags: (conversations) => [
          PERSONA_CHAT_CONVERSATIONS_LIST_TAG,
          ...(conversations ?? []).map(({ id }) => ({
            type: 'PersonaChatConversations' as const,
            id,
          })),
        ],
      },
      getOrganizationsByOrgIdPersonaPanelsAndPersonaPanelIdChatConversationsConversationId: {
        providesTags: (_result, _error, arg) => [
          { type: 'PersonaChatConversations' as const, id: arg.conversationId },
        ],
      },
      patchOrganizationsByOrgIdPersonaPanelsAndPersonaPanelIdChatConversationsConversationId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'PersonaChatConversations' as const, id: arg.conversationId },
          PERSONA_CHAT_CONVERSATIONS_LIST_TAG,
        ],
      },
      deleteOrganizationsByOrgIdPersonaPanelsAndPersonaPanelIdChatConversationsConversationId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'PersonaChatConversations' as const, id: arg.conversationId },
          PERSONA_CHAT_CONVERSATIONS_LIST_TAG,
        ],
      },

      // Customers enhancer (inlined)
      getCustomers: {
        providesTags: (customers) =>
          customers
            ? [
                ...customers.map(({ id }) => ({
                  type: TAG_TYPES.CUSTOMER,
                  id,
                })),
                TAG_TYPES.CUSTOMER,
              ]
            : [TAG_TYPES.CUSTOMER],
      },

      getTeamsByTeamIdCustomers: {
        providesTags: (customers) =>
          customers
            ? [
                ...customers.map(({ id }) => ({
                  type: TAG_TYPES.CUSTOMER,
                  id,
                })),
                TAG_TYPES.CUSTOMER,
              ]
            : [TAG_TYPES.CUSTOMER],
      },

      // File Answers enhancer (inlined)
      getProjectsByProjectIdFileAnswers: {
        providesTags: () => [TAG_TYPES.FILE_ANSWER],
      },
      postProjectsByProjectIdFileAnswers: {
        invalidatesTags: () => [TAG_TYPES.FILE_ANSWER],
      },

      // Inserts enhancer (inlined)
      getProjectsByProjectIdInserts: {
        providesTags: () => [TAG_TYPES.INSERT],
      },

      // Kits enhancer (inlined)
      getProjectsByProjectIdKits: {
        providesTags: (kits) =>
          kits
            ? [
                ...kits.map(({ id }) => ({
                  type: TAG_TYPES.KIT,
                  id,
                })),
                TAG_TYPES.KIT,
              ]
            : [TAG_TYPES.KIT],
      },

      // Links enhancer (inlined)
      getProjectsByProjectIdLinks: {
        providesTags: (links) =>
          links?.length
            ? links.map(({ projectLinkId }) => ({
                type: TAG_TYPES.PROJECT_LINK,
                id: projectLinkId,
              }))
            : [TAG_TYPES.PROJECT_LINK],
      },

      // Modules enhancer (inlined)
      postModulesByModuleIdBlocksAssign: {
        invalidatesTags: [TAG_TYPES.MODULE],
      },
      postBlueprints: {
        invalidatesTags: [TAG_TYPES.MODULE],
      },
      getBlueprints: {
        providesTags: [TAG_TYPES.BLUEPRINTS_CSV],
        query: (arg) => {
          return {
            url: `/blueprints?solutionId=${arg.solutionId}&surveyRoleId=${arg.surveyRoleId}&subject=${arg.subject}&testCategoryId=${arg.testCategoryId}`,
            cache: 'no-cache',
            responseHandler: async (response) => response.text(),
          };
        },
      },

      // Module Configurations enhancer (inlined)
      postBlueprintsModuleconfigurations: {
        invalidatesTags: [TAG_TYPES.MODULE_CONFIGURATION],
      },

      // Notification Unsubscriptions enhancer (inlined)
      getProjectsByProjectIdNotificationUnsubscriptions: {
        providesTags: (notificationUnsubscriptions) =>
          notificationUnsubscriptions
            ? [
                ...notificationUnsubscriptions.map(({ id }) => ({
                  type: TAG_TYPES.NOTIFICATION_UNSUBSCRIPTION,
                  id: id!,
                })),
                TAG_TYPES.NOTIFICATION_UNSUBSCRIPTION,
              ]
            : [TAG_TYPES.NOTIFICATION_UNSUBSCRIPTION],
      },
      // Orgs enhancer (inlined)
      getOrganizations: {
        providesTags: (orgs) =>
          orgs
            ? [
                ...orgs.map(({ id }) => ({
                  type: TAG_TYPES.ORG,
                  id,
                })),
                TAG_TYPES.ORG,
              ]
            : [TAG_TYPES.ORG],
        transformResponse: (response: Organization[]) => {
          return response.sort((a, b) => {
            if (a.name && b.name) {
              return a.name.localeCompare(b.name); // Sorting by name alphabetically
            }
            return 0; // To appease the TS
          });
        },
      },
      postOrganizations: {
        invalidatesTags: [TAG_TYPES.ORG],
      },
      putOrganizations: {
        invalidatesTags: [TAG_TYPES.ORG],
      },

      // Presentation Rules enhancer (inlined)
      getProjectsByProjectIdPresentationrules: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PRESENTATION_RULES,
            id: arg.projectId,
          },
        ],
      },
      // Products enhancer (inlined)
      getProductsByProductId: {
        providesTags: (product) =>
          product ? [{ type: TAG_TYPES.PRODUCT, id: product.id }] : [TAG_TYPES.PRODUCT],
      },

      getProjectsByProjectIdProducts: {
        providesTags: (products) =>
          products
            ? [
                ...products.map(({ id }) => ({
                  type: TAG_TYPES.PRODUCT,
                  id,
                })),
                TAG_TYPES.PRODUCT,
              ]
            : [TAG_TYPES.PRODUCT],
        transformResponse: (response: Product[]) =>
          [...(response ?? [])].sort(({ id: a = 0 }, { id: b = 0 }) => a - b),
      },

      putProductsByProductId: {
        invalidatesTags: [TAG_TYPES.PRODUCT, TAG_TYPES.PRODUCT_HANDLING_PREFERENCE],
      },

      // Read-only: a plan must not refetch the products it was planned for.
      postProjectsByProjectIdProductsSurveyRenamePlan: {
        invalidatesTags: () => [],
      },

      // Product Project Assets enhancer (inlined)
      getProjectsByProjectIdProductProjectAssets: {
        providesTags: (productProjectAssets) =>
          productProjectAssets
            ? [
                ...productProjectAssets.map(({ id }) => ({
                  type: TAG_TYPES.PRODUCT_PROJECT_ASSETS,
                  id,
                })),
                TAG_TYPES.PRODUCT_PROJECT_ASSETS,
              ]
            : [TAG_TYPES.PRODUCT_PROJECT_ASSETS],
      },
      patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds:
        patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds(
          _projectApi as unknown as Parameters<
            typeof patchSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdBlockIds
          >[0],
        ),

      // Project Surveys enhancer (inlined)
      deleteProjectsByProjectIdSurveysAndSurveyId: {
        invalidatesTags: [TAG_TYPES.PROJECT, TAG_TYPES.PROJECT_SURVEY_CONFIG],
      },

      patchProjectsByProjectIdFieldingGroupAndFieldingGroupId:
        patchProjectsByProjectIdFieldingGroupAndFieldingGroupId(_projectApi),

      patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus:
        patchProjectsByProjectIdSurveysAndSurveyIdSurveyStatus(_projectApi),

      // Projects enhancer (inlined)
      getProjects: {
        providesTags: (projects) =>
          projects
            ? [
                ...projects.map(({ id }) => ({
                  type: TAG_TYPES.PROJECT,
                  id: id!,
                })),
                TAG_TYPES.PROJECT,
              ]
            : [TAG_TYPES.PROJECT],
      },
      getProjectsByProjectId: {
        providesTags: (project) => [
          {
            type: TAG_TYPES.PROJECT,
            id: project?.id ?? undefined,
          },
        ],
      },

      postProjectsConfigure: {
        invalidatesTags: (project) => [
          {
            type: TAG_TYPES.PROJECT,
            id: project?.id ?? undefined,
          },
        ],
      },

      postProjectsByProjectIdAssignAdmins: {
        invalidatesTags: (_, __, arg) => [
          {
            type: TAG_TYPES.PROJECT,
            id: arg.projectId,
          },
          {
            type: TAG_TYPES.ADMIN,
          },
        ],
      },

      patchProjectsByProjectIdStatus: {
        invalidatesTags: (_, __, arg) => [
          {
            type: TAG_TYPES.PROJECT,
            id: arg.projectId,
          },
        ],
      },
      postProjectsCreateFromExisting: {
        invalidatesTags: [TAG_TYPES.PROJECT, TAG_TYPES.PROJECT_SURVEY_CONFIG],
      },
      patchProjectsByProjectIdName: {
        invalidatesTags: [TAG_TYPES.PROJECT, TAG_TYPES.PROJECT_SURVEY_CONFIG],
      },

      getProjectsAdminProjects: {
        providesTags: () => [
          {
            type: TAG_TYPES.PROJECT,
          },
          {
            type: TAG_TYPES.ADMIN,
          },
        ],
      },
      postProjectsByProjectIdFieldingGroupFromTestGroups: {
        invalidatesTags: ['FieldingGroup', TAG_TYPES.KIT],
      },
      // The wipe takes the fielding groups' kits with it, which the generated tag misses. It also
      // clears inserts and product lists, which no client-app query reads.
      postProjectsByProjectIdFieldingGroupDeleteAllEntities: {
        invalidatesTags: ['FieldingGroup', TAG_TYPES.KIT],
      },
      postFieldingGroupsByFieldingGroupIdSteps: {
        invalidatesTags: ['Steps', 'FieldingGroup'],
      },
      patchFieldingGroupsByFieldingGroupIdStepsAndStepId:
        patchFieldingGroupsByFieldingGroupIdStepsAndStepId(_projectApi),
      deleteFieldingGroupsByFieldingGroupIdStepsAndStepId: {
        invalidatesTags: ['Steps', 'FieldingGroup'],
      },
      patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals:
        patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals(_projectApi),

      // Delivery Information enhancer
      postFieldingGroupsByFieldingGroupIdFieldingGroupAssignments: {
        invalidatesTags: ['FieldingGroupAssignments', 'FieldingGroup'],
      },
      deleteFieldingGroupsByFieldingGroupIdFieldingGroupAssignments: {
        invalidatesTags: ['FieldingGroupAssignments', 'FieldingGroup'],
      },

      // Quality Control Report enhancer (inlined)
      getProjectsByProjectIdQualityControlReportFullCompletesBreakdown: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.QUALITY_CONTROL_RESULTS, id: arg.projectId },
        ],
      },
      getProjectsByProjectIdQualityControlReportAndResponseId: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.QUALITY_CONTROL_RESULTS_RESPONSES, id: arg.responseId },
          TAG_TYPES.QUALITY_CONTROL_RESULTS_RESPONSES,
        ],
      },
      postProjectsByProjectIdQualityControlReportSetAnswerExcluded: {
        invalidatesTags: [TAG_TYPES.QUALITY_CONTROL_RESULTS_RESPONSES],
      },

      // Results enhancer (inlined)
      getProjectsByProjectIdResultsDownloadcsv: {
        providesTags: [TAG_TYPES.RESULTS_CSV],
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/Results/$downloadcsv`,
          params: {
            selectedSurveyConfigIds: arg.selectedSurveyConfigIds,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },
      getProjectsByProjectIdResultsDownloadcodedcsv: {
        providesTags: [TAG_TYPES.RESULTS_CODED_CSV],
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/Results/$downloadcodedcsv`,
          params: {
            selectedSurveyConfigIds: arg.selectedSurveyConfigIds,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },
      getProjectsByProjectIdResultsDownloadcsvallrespondents: {
        providesTags: [TAG_TYPES.RESULTS_CODED_CSV_ALL_RESPONDENTS],
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/Results/$downloadcsvallrespondents`,
          params: {
            selectedSurveyConfigIds: arg.selectedSurveyConfigIds,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },
      getProjectsByProjectIdResultsDownloadcodedcsvallrespondents: {
        providesTags: [TAG_TYPES.RESULTS_CODED_CSV_ALL_RESPONDENTS],
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/Results/$downloadcodedcsvallrespondents`,
          params: {
            selectedSurveyConfigIds: arg.selectedSurveyConfigIds,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },
      postProjectsByProjectIdResultsDownloadcrosstabs: {
        query: (arg) => ({
          method: 'POST',
          url: `/Projects/${arg.projectId}/Results/$downloadcrosstabs`,
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
          body: arg.crossTabParams,
        }),
      },
      postProjectsByProjectIdResultsDownloadscorecardcsv: {
        query: (arg) => ({
          method: 'POST',
          url: `/Projects/${arg.projectId}/Results/$downloadscorecardcsv`,
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
          body: arg.downloadScorecardParams,
        }),
      },
      postProjectsByProjectIdResultsDownloadpenaltyanalysis: {
        query: (arg) => ({
          method: 'POST',
          url: `/Projects/${arg.projectId}/Results/$downloadpenaltyanalysis`,
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
          body: arg.penaltyParams,
        }),
      },
      postProjectsByProjectIdReportAndGeneratedReportIdDownloadReportDownloadType: {
        query: (arg) => ({
          method: 'POST',
          url: `/Projects/${arg.projectId}/Report/${arg.generatedReportId}/$download/${arg.reportDownloadType}`,
          cache: 'no-cache',
          responseHandler: async (response) => response.blob(),
        }),
        transformResponse: (response: Blob) => response,
      },
      getProjectsByProjectIdSurveyGroups: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_GROUPS,
            id: arg.projectId,
          },
        ],
      },
      getProjectsByProjectIdSurveyGroupsProductGroups: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PRODUCT_GROUPS,
            id: arg.projectId,
          },
        ],
      },
      getProjectsByProjectIdResultContexts: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.RESULT_CONTEXT,
            id: arg.projectId,
          },
        ],
      },
      putProjectsByProjectIdSurveyGroupsProductGroups: {
        invalidatesTags: [
          TAG_TYPES.PRODUCT_GROUPS,
          TAG_TYPES.SURVEY_GROUPS,
          ...RESULTS_INVALIDATION_TAGS,
        ],
      },
      deleteProjectsByProjectIdSurveyGroupsProductGroupsAndProductGroupId: {
        invalidatesTags: [
          TAG_TYPES.PRODUCT_GROUPS,
          TAG_TYPES.SURVEY_GROUPS,
          ...RESULTS_INVALIDATION_TAGS,
        ],
      },
      putProjectsByProjectIdSurveyGroups: {
        invalidatesTags: [TAG_TYPES.SURVEY_GROUPS, ...RESULTS_INVALIDATION_TAGS],
      },
      postProjectsByProjectIdQuestionsAutoLink: {
        invalidatesTags: [
          'Projects',
          TAG_TYPES.SURVEY_GROUPS,
          TAG_TYPES.PRODUCT_GROUPS,
          ...RESULTS_INVALIDATION_TAGS,
        ],
      },
      getProjectsByProjectIdSurveyGroupsEnabled: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_GROUPS,
            id: arg.projectId,
          },
        ],
      },
      getProjectsByProjectIdSurveyGroupsSeparable: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_GROUPS,
            id: arg.projectId,
          },
        ],
      },
      getProjectsByProjectIdSegments: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SEGMENTS,
            id: arg.projectId,
          },
        ],
      },
      getProjectsByProjectIdSegmentsEnabled: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SEGMENTS,
            id: arg.projectId,
          },
        ],
      },
      putProjectsByProjectIdSegmentsUpsert: {
        invalidatesTags: [
          TAG_TYPES.SEGMENTS,
          TAG_TYPES.SEGMENT_COUNTS,
          ...RESULTS_INVALIDATION_TAGS,
        ],
      },
      deleteProjectsByProjectIdSegmentsAndId: {
        invalidatesTags: [TAG_TYPES.SEGMENTS, ...RESULTS_INVALIDATION_TAGS],
      },
      getProjectsByProjectIdResultsmeta: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.RESULTS_META, id: arg.projectId },
        ],
      },
      getProjectsByProjectIdSegmentsAndSegmentIdCounts: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.SEGMENT_COUNTS, id: arg.segmentId },
        ],
      },
      postProjectsByProjectIdResultsGetsummary: {
        query: (arg) => ({
          method: 'POST',
          url: `/Projects/${arg.projectId}/Results/$getsummary`,
          body: arg.getSummaryProjectApiRequest,
        }),
      },

      // Results query cache tags (INS-581)
      getProjectsByProjectIdResultsExploreresults: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.EXPLORE_RESULTS, id: arg.projectId },
          TAG_TYPES.EXPLORE_RESULTS,
        ],
        keepUnusedDataFor: 300,
      },
      getProjectsByProjectIdResultsGetcrosstabdata: {
        providesTags: (_result, _error, arg) => [
          { type: TAG_TYPES.CROSSTABS_DATA, id: arg.projectId },
          TAG_TYPES.CROSSTABS_DATA,
        ],
        keepUnusedDataFor: 300,
      },

      // Packing List enhancer (inlined)
      getProjectsByProjectIdPackingListBoxConfigurations: {
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/PackingList/$boxConfigurations`,
          params: {
            kitId: arg.kitId,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },
      getProjectsByProjectIdPackingListSimplifiedBoxList: {
        query: (arg) => ({
          url: `/Projects/${arg.projectId}/PackingList/$simplifiedBoxList`,
          params: {
            kitId: arg.kitId,
          },
          cache: 'no-cache',
          responseHandler: async (response) => response.text(),
        }),
      },

      // Slot Values enhancer (inlined)
      postProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesV2: {
        invalidatesTags: () => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
          },
          {
            type: TAG_TYPES.PROJECT_ATTACHMENT,
          },
        ],
      },
      postProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesBulkAssign: {
        invalidatesTags: () => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
          },
          {
            type: TAG_TYPES.PROJECT_ATTACHMENT,
          },
        ],
      },
      deleteProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesSlotValueId: {
        invalidatesTags: () => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
          },
        ],
      },

      // Survey Bundles enhancer (inlined)
      getProjectsByProjectIdSurveyBundles: {
        providesTags: (_result, _error, { projectId }) => [
          { type: TAG_TYPES.SURVEY_BUNDLES, id: projectId },
        ],
      },
      postProjectsByProjectIdSurveyBundles: {
        invalidatesTags: (_result, _error, { projectId }) => [
          { type: TAG_TYPES.SURVEY_BUNDLES, id: projectId },
        ],
      },
      putProjectsByProjectIdSurveyBundlesAndSurveyBundleId: {
        invalidatesTags: (_result, _error, { projectId }) => [
          { type: TAG_TYPES.SURVEY_BUNDLES, id: projectId },
        ],
      },

      // Survey Config Blocks enhancer (inlined)
      getProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocks: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigId,
          },
        ],
      },
      postProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksAssign: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigId,
          },
        ],
      },
      postSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdClone: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigId,
          },
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCK_GROUPS,
          },
        ],
      },
      putProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksSurveyConfigBlockId: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigId,
          },
        ],
      },
      putProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocks: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigId,
          },
        ],
      },

      // Survey Config Question Choices enhancer (inlined)
      postProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdChoicesSurveyConfigQuestionChoiceId:
        {
          invalidatesTags: (_result, _error, arg) => [
            {
              type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
              id: arg.surveyConfigId,
            },
            {
              type: TAG_TYPES.PROJECT_ATTACHMENT,
            },
          ],
        },

      // Survey Config Question Routings enhancer (inlined)
      getProjectsByProjectIdSurveyConfigsAndSurveyConfigIdRoutings: {
        providesTags: (routings) =>
          routings
            ? [
                ...routings.map(({ sourceSurveyConfigQuestionId }) => ({
                  type: TAG_TYPES.SURVEY_CONFIG_QUESTION_ROUTING,
                  id: sourceSurveyConfigQuestionId,
                })),
                TAG_TYPES.SURVEY_CONFIG_QUESTION_ROUTING,
              ]
            : [TAG_TYPES.SURVEY_CONFIG_QUESTION_ROUTING],
      },
      getProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdRoutings: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_QUESTION_ROUTING,
            id: arg.surveyConfigQuestionId,
          },
        ],
      },

      // Survey Configs enhancer (inlined)
      getProjectsByProjectIdSurveyConfigs: {
        providesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PROJECT_SURVEY_CONFIG,
            id: arg.projectId,
          },
        ],
      },

      putProjectsByProjectIdSurveyConfigsAndSurveyConfigId: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PROJECT_SURVEY_CONFIG,
            id: arg.projectId,
          },
        ],
      },

      postProjectsByProjectIdSurveyConfigsConfigure: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PROJECT_SURVEY_CONFIG,
            id: arg.projectId,
          },
        ],
      },

      postProjectsByProjectIdSurveyConfigsAndSurveyConfigIdMaterialize: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.PROJECT_SURVEY_CONFIG,
            id: arg.projectId,
          },
        ],
      },

      postProjectsByProjectIdSurveyConfigsCopySurveyContent: {
        invalidatesTags: (_result, _error, arg) => [
          {
            type: TAG_TYPES.SURVEY_CONFIG_BLOCKS,
            id: arg.surveyConfigContentCopyRequest.targetSurveyConfigId,
          },
          TAG_TYPES.SURVEY_CONFIG_BLOCK_GROUPS,
          TAG_TYPES.SURVEY_CONFIG_QUESTION_ROUTING,
        ],
      },

      // Teams enhancer (inlined)
      getTeams: {
        providesTags: (teams) =>
          teams
            ? [
                ...teams.map(({ teamId }) => ({
                  type: TAG_TYPES.TEAM,
                  id: teamId,
                })),
                TAG_TYPES.TEAM,
              ]
            : [TAG_TYPES.TEAM],
      },
      postTeams: {
        invalidatesTags: [TAG_TYPES.TEAM, TAG_TYPES.ORG],
      },
      getTeamsByTeamId: {
        providesTags: (_result, _error, arg) => [{ type: TAG_TYPES.TEAM, id: arg.teamId }],
      },
      postTeamsByTeamIdCustomersAndCustomerId: {
        invalidatesTags: [TAG_TYPES.CUSTOMER, TAG_TYPES.TEAM],
      },
      deleteTeamsByTeamIdCustomersAndCustomerId: {
        invalidatesTags: [TAG_TYPES.CUSTOMER, TAG_TYPES.TEAM],
      },

      // Subscriptions enhancer (inlined)
      getTeamsByTeamIdSubscriptions: {
        providesTags: (subscriptions) =>
          subscriptions
            ? [
                ...subscriptions.map(({ id }) => ({
                  type: TAG_TYPES.SUBSCRIPTION,
                  id,
                })),
                TAG_TYPES.SUBSCRIPTION,
              ]
            : [TAG_TYPES.SUBSCRIPTION],
      },
      postTeamsByTeamIdSubscriptions: {
        invalidatesTags: [TAG_TYPES.SUBSCRIPTION],
      },
      putTeamsByTeamIdSubscriptionsAndSubscriptionId: {
        invalidatesTags: [TAG_TYPES.SUBSCRIPTION],
      },
      deleteTeamsByTeamIdSubscriptionsAndSubscriptionId: {
        invalidatesTags: [TAG_TYPES.SUBSCRIPTION],
      },

      // HubSpot Deals enhancer (inlined)
      getHubSpotDealsByDealId: {
        providesTags: (result) =>
          result?.id ? [{ type: TAG_TYPES.HUBSPOT_DEAL, id: result.id }] : [],
      },

      // Show Logics enhancer (inlined)
      deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId:
        deleteSurveyconfigsBySurveyConfigIdShowlogicsAndId(_projectApi),
      putSurveyconfigsBySurveyConfigIdShowlogicsAndId:
        putSurveyconfigsBySurveyConfigIdShowlogicsAndId(_projectApi),

      getShowlogicsAllowedops: {
        keepUnusedDataFor: Infinity, // Static, no need to invalidate
        providesTags: () => [TAG_TYPES.SHOW_LOGIC_ALLOWED_OPS],
      },

      // AI Summary: cast the generated `unknown` GET response to ProjectSummaryResponse
      getProjectsByProjectIdAiSummary: {
        transformResponse: (response: ProjectSummaryResponse) => response,
      },

      // Custom topics: the generated mutations only invalidate ['CustomTopics'], which
      // refreshes the topic list. Adding/removing a topic also changes the explore payload
      // (per-response assignments and counts), so additionally invalidate the results tags
      // to refetch the open-ended panel live — matching the pre-generated hand-rolled behavior.
      postProjectsByProjectIdResultsCustomTopics: {
        invalidatesTags: ['CustomTopics', ...RESULTS_INVALIDATION_TAGS],
      },
      deleteProjectsByProjectIdResultsCustomTopicsAndId: {
        invalidatesTags: ['CustomTopics', ...RESULTS_INVALIDATION_TAGS],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // All team customers including disabled (for CSM admin browsing)
      getAllTeamCustomers: build.query<Types.Customer[], { teamId: number }>({
        query: (queryArg) => ({
          url: `/Teams/${queryArg.teamId}/Customers`,
          params: { includeDisabled: true },
        }),
        providesTags: (customers) =>
          customers
            ? [
                ...customers.map(({ id }) => ({
                  type: TAG_TYPES.CUSTOMER,
                  id,
                })),
                TAG_TYPES.CUSTOMER,
              ]
            : [TAG_TYPES.CUSTOMER],
      }),

      // All organizations including deleted (for CSM admin browsing)
      getAllOrganizations: build.query<Types.Organization[], void>({
        query: () => ({ url: '/Organizations', params: { includeDeleted: true } }),
        providesTags: [TAG_TYPES.ORG],
      }),

      // All teams by org including deleted (for CSM admin browsing)
      getAllTeamsByOrg: build.query<Types.Team[], { organizationId: number }>({
        query: ({ organizationId }) => ({
          url: '/Teams',
          params: { organizationId, includeDeleted: true },
        }),
        providesTags: (teams) =>
          teams
            ? [
                ...teams.map(({ teamId }) => ({
                  type: TAG_TYPES.TEAM,
                  id: teamId,
                })),
                TAG_TYPES.TEAM,
              ]
            : [TAG_TYPES.TEAM],
      }),

      // Get customers by organization (for CSM admin team membership management)
      getOrgCustomers: build.query<Types.Customer[], { organizationId: number }>({
        query: ({ organizationId }) => ({
          url: '/Customers',
          params: { organizationId },
        }),
        providesTags: (customers) =>
          customers
            ? [
                ...customers.map(({ id }) => ({
                  type: TAG_TYPES.CUSTOMER,
                  id,
                })),
                TAG_TYPES.CUSTOMER,
              ]
            : [TAG_TYPES.CUSTOMER],
      }),

      // Batch AI summary endpoint (POST-based read — uses queryFn so it gets
      // caching, polling, and deduplication that mutations lack)
      getBatchSummary: build.query<
        FetchSchemas['GetBatchSummaryResponse'],
        {
          projectId: number;
          lineItemIds: string[];
          segmentIds: string[];
          surveyGroupIds: string[];
          resultContextId?: string;
          positionFilter?: number;
        }
      >({
        queryFn: async (
          { projectId, lineItemIds, segmentIds, surveyGroupIds, resultContextId, positionFilter },
          _api,
          _extraOptions,
          baseQuery,
        ) => {
          const result = await baseQuery({
            url: `/Projects/${projectId}/Results/$getbatchsummary`,
            method: 'POST',
            body: { lineItemIds, segmentIds, surveyGroupIds, resultContextId, positionFilter },
          });
          if (result.error) {
            return { error: result.error };
          }
          return { data: result.data as FetchSchemas['GetBatchSummaryResponse'] };
        },
        serializeQueryArgs: ({ queryArgs }) => {
          const sortedLineItems = [...queryArgs.lineItemIds].sort().join(',');
          const sortedSegments = [...queryArgs.segmentIds].sort().join(',');
          const sortedGroups = [...queryArgs.surveyGroupIds].sort().join(',');
          return `getBatchSummary(${queryArgs.projectId}-${sortedLineItems}-${sortedSegments}-${sortedGroups}-${queryArgs.resultContextId ?? ''}-${queryArgs.positionFilter ?? ''})`;
        },
        providesTags: (_result, _error, { projectId }) => [
          { type: TAG_TYPES.SUMMARIES, id: projectId },
        ],
      }),

      // Feasibility estimate endpoint (hits Next.js route, not C# API)
      computeFeasibilityEstimate: build.query<
        ComputeFeasibilityEstimateApiResponse,
        ComputeFeasibilityEstimateApiArg
      >({
        queryFn: async ({ projectId, fractionalIrReasoning }) => {
          try {
            const res = await fetch('/api/feasibility/estimate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId, fractionalIrReasoning }),
            });
            if (!res.ok) {
              return { error: { status: res.status, data: await res.text() } };
            }
            return { data: await res.json() };
          } catch (err) {
            return { error: { status: 'FETCH_ERROR', error: String(err) } };
          }
        },
        // Only use projectId + audienceHash (+ fractional flag) for cache key
        // (exclude full audience object). The fractional preview is cached separately.
        serializeQueryArgs: ({ queryArgs }) =>
          `computeFeasibilityEstimate(${queryArgs.projectId}-${queryArgs.audienceHash ?? ''}-${
            queryArgs.fractionalIrReasoning ? 'fractional' : 'legacy'
          })`,
        providesTags: [TAG_TYPES.FEASIBILITY_ESTIMATE],
      }),
    }),
  });

/**
 * @deprecated use exports from {@link projectApi} directly instead
 */
export const {
  useGetAdminsQuery,
  useLazyGetAdminsQuery,
  usePostAdminsMutation: useCreateAdminMutation,
  usePostByIdDisableMutation: useDisableAdminMutation,
  useGetProjectsByProjectIdAdminsQuery: useGetAdminsByProjectQuery,
  useGetProjectsByProjectIdAttachmentsQuery: useGetProjectAttachmentsByProjectIdQuery,
  useLazyGetProjectsByProjectIdAttachmentsQuery: useLazyGetProjectAttachmentsByProjectIdQuery,
  usePostProjectsByProjectIdAttachmentsMutation: useUploadProjectAttachmentMutation,
  usePutProjectsByProjectIdAttachmentsAndAttachmentIdMutation: useUpdateProjectAttachmentMutation,
  usePostProjectsByProjectIdSurveyConfigsConfigureMutation: useConfigureSurveyConfigMutation,
  useDeleteProjectsByProjectIdAttachmentsAndProjectAttachmentIdMutation:
    useDeleteProjectAttachmentMutation,
  useGetProjectsByProjectIdAudienceBuilderAudienceQuery: useGetAudienceBuilderAudienceQuery,
  usePutProjectsByProjectIdAudienceBuilderAudienceMutation: usePutAudienceBuilderAudienceMutation,
  useGetProjectsByProjectIdAudienceAttachmentsAndAttachmentIdQuery:
    useGetAudienceAttachmentsAndAttachmentIdQuery,
  usePostProjectsByProjectIdAudienceAttachmentsMutation: usePostAudienceAttachmentsMutation,
  useGetTeamsByTeamIdCustomersQuery,
  useGetTeamsByTeamIdCustomersQuery: useGetActiveTeamCustomersQuery,
  useGetAllTeamCustomersQuery,
  useGetAllOrganizationsQuery,
  useGetAllTeamsByOrgQuery,
  useGetOrgCustomersQuery,
  usePostTeamsByTeamIdCustomersAndCustomerIdMutation: useAddCustomerToTeamMutation,
  useDeleteTeamsByTeamIdCustomersAndCustomerIdMutation: useRemoveCustomerFromTeamMutation,
  useGetTeamsByTeamIdSubscriptionsQuery: useGetTeamSubscriptionsQuery,
  usePostTeamsByTeamIdSubscriptionsMutation: useCreateSubscriptionMutation,
  usePutTeamsByTeamIdSubscriptionsAndSubscriptionIdMutation: useUpdateSubscriptionMutation,
  useDeleteTeamsByTeamIdSubscriptionsAndSubscriptionIdMutation: useDeleteSubscriptionMutation,
  useGetTeamsByTeamIdSubscriptionBalanceQuery: useGetSubscriptionBalanceQuery,
  useGetHubSpotDealsByDealIdQuery: useGetHubSpotDealQuery,
  useLazyGetHubSpotDealsByDealIdQuery: useLazyGetHubSpotDealQuery,
  usePutCustomersMutation: useUpdateCustomerMutation,

  useGetProjectsByProjectIdFileAnswersQuery: useGetProjectZipQuery,
  useLazyGetProjectsByProjectIdFileAnswersQuery: useLazyGetProjectZipQuery,
  usePostProjectsByProjectIdFileAnswersMutation: useGenerateProjectZipMutation,
  useGetProjectsByProjectIdInsertsQuery: useGetInsertsByProjectId,
  usePostInsertsMutation: useCreateInsertMutation,
  usePostInsertsByInsertIdFileMutation: useCreateInsertFile,
  useDeleteInsertsByInsertIdMutation: useDeleteInsertById,
  usePutInsertsByInsertIdMutation: useUpdateInsertById,
  useDeleteInsertsByInsertIdFileMutation: useDeleteInsertFileById,
  useGetProjectsByProjectIdKitsQuery: useGetKitsQuery,
  useLazyGetProjectsByProjectIdKitsQuery: useLazyGetKitsQuery,
  usePostProjectsByProjectIdKitsMutation: useCreateKitMutation,
  usePutProjectsByProjectIdKitsAndKitIdMutation: useUpdateKitMutation,
  useDeleteProjectsByProjectIdKitsAndKitIdMutation: useDeleteKitMutation,
  useGetProjectsByProjectIdLinksQuery: useGetProjectLinksByProjectIdQuery,
  useLazyGetProjectsByProjectIdLinksQuery: useLazyGetProjectLinksByProjectIdQuery,
  usePostProjectsByProjectIdLinksMutation: useCreateProjectLinkMutation,
  usePutProjectsByProjectIdLinksAndProjectLinkIdMutation: useUpdateProjectLinkMutation,
  useDeleteProjectsByProjectIdLinksAndProjectLinkIdMutation: useDeleteProjectLinkMutation,
  useLazyGetModulesQuery,
  useGetModulesQuery,
  useGetModulesByModuleIdQuery,
  usePostModulesMutation,
  usePostModulesByModuleIdBlocksAssignMutation,
  usePutModulesMutation,
  usePostBlueprintsMutation,
  useGetBlueprintCatalogExportQuery,
  usePostBlueprintCatalogApplyMutation,
  useLazyGetBlueprintsQuery: useLazyDownloadBlueprintCsv,
  useGetModuleConfigurationsQuery,
  usePostBlueprintsModuleconfigurationsMutation: useImportModuleConfigsMutation,
  usePostModuleConfigurationsAssignMutation: useAssignModuleConfigsMutation,
  useGetProjectsByProjectIdNotificationUnsubscriptionsQuery:
    useGetProjectNotificationUnsubscriptionsQuery,
  usePostProjectsByProjectIdNotificationUnsubscriptionsMutation:
    useCreateNotificationUnsubscriptionMutation,
  useDeleteProjectsByProjectIdNotificationUnsubscriptionsMutation:
    useDeleteNotificationUnsubscriptionMutation,
  useGetOrganizationsQuery,
  usePostOrganizationsMutation: useCreateOrganizationMutation,
  useGetProjectsByProjectIdPresentationrulesQuery: useGetPresentationRuleDtos,
  usePutProjectsByProjectIdPresentationrulesAndSlugMutation: useSavePresentationRule,
  useDeleteProjectsByProjectIdPresentationrulesAndSlugMutation: useDeletePresentationRule,
  useGetProductsByProductIdQuery: useGetProductByIdQuery,
  useLazyGetProductsByProductIdQuery: useLazyGetProductByIdQuery,
  useGetProjectsByProjectIdProductsQuery: useGetProductsQuery,
  useLazyGetProjectsByProjectIdProductsQuery: useLazyGetProductsQuery,
  // Legacy per-row writes. The project builder saves through $bulk via data-access/repositories/products.ts.
  usePostProjectsByProjectIdProductsMutation: useCreateProductByProjectIdMutation,
  usePutProjectsByProjectIdProductsMutation: useUpdateProductMutation,
  usePutProductsByProductIdMutation: useUpdateProductAndHandlingMutation,
  useDeleteProjectsByProjectIdProductsMutation: useDeleteProductMutation,
  usePostProjectsByProjectIdProductsSurveyRenamePlanMutation: usePlanProductSurveyRenamesMutation,
  useGetProjectsByProjectIdProductProjectAssetsQuery: useGetProductProjectAssetsByProjectIdQuery,
  usePostProjectsByProjectIdProductsAndProductIdProductProjectAssetsMutation:
    useCreateProductProjectAssetMutation,
  useDeleteProductProjectAssetsByProductProjectAssetIdMutation:
    useDeleteProductProjectAssetMutation,
  useDeleteProjectsByProjectIdSurveysAndSurveyIdMutation: useDeleteProjectSurveyMutation,
  usePatchProjectsByProjectIdSurveysAndSurveyIdSurveyStatusMutation:
    usePatchProjectSurveyStatusMutation,
  useGetProjectCategoriesQuery,
  useGetProjectsQuery,
  useGetProjectsByProjectIdConfigurationsQuery: useGetProjectConfigurations,
  usePatchProjectsByProjectIdTeamMutation: usePatchProjectTeamMutation,
  useDeleteProjectsByProjectIdMutation: useDeleteProjectMutation,
  usePostProjectsByProjectIdAssignAdminsMutation: useAssignProjectAdminsMutation,
  usePostProjectsConfigureMutation: useConfigureProjectMutation,
  useGetProjectsByProjectIdQuery: useGetProjectByIdQuery,
  useLazyGetProjectsByProjectIdQuery: useLazyGetProjectByIdQuery,
  useLazyGetProjectsQuery: useLazyGetProjectByPublicIdQuery,
  usePutProjectsMutation: useUpdateProjectMutation,
  usePatchProjectsByProjectIdStatusMutation: useUpdateProjectStatusByIdMutation,
  usePatchProjectsByProjectIdProductBlindingMethodMutation:
    useUpdateProjectProductBlindingMethodMutation,
  useGetProjectsByProjectIdQualityControlReportQuery: useGetQualityControlReportQuery,
  useLazyGetProjectsByProjectIdQualityControlReportQuery: useLazyGetQualityControlReportQuery,
  useGetProjectsByProjectIdQualityControlReportAndResponseIdQuery:
    useGetQualityControlResponseListQuery,
  useLazyGetProjectsByProjectIdQualityControlReportAndResponseIdQuery:
    useLazyGetQualityControlResponseListQuery,
  usePostProjectsByProjectIdQualityControlReportSetAnswerExcludedMutation:
    useQualityControlSetAnswerExcludedMutation,
  useGetProjectsByProjectIdRecruitingDataQuery: useGetRecruitingDataQuery,
  useLazyGetProjectsByProjectIdRecruitingDataQuery: useLazyGetRecruitingDataQuery,
  usePostProjectsByProjectIdRecruitingDataMutation: useCreateRecruitingDataMutation,
  usePutProjectsByProjectIdRecruitingDataMutation: useUpdateRecruitingDataMutation,
  usePostProjectsByProjectIdRecruitingDataBoostMutation: useBoostRecruitingMutation,
  useLazyGetProjectsByProjectIdResultsDownloadcsvQuery: useLazyDownloadProjectResultsCsv,
  useLazyGetProjectsByProjectIdResultsDownloadcodedcsvQuery: useLazyDownloadProjectResultsCodedCsv,
  useLazyGetProjectsByProjectIdResultsDownloadcsvallrespondentsQuery:
    useLazyDownloadProjectResultsCsvAllRespondents,
  useLazyGetProjectsByProjectIdResultsDownloadcodedcsvallrespondentsQuery:
    useLazyDownloadProjectResultsCodedCsvAllRespondents,
  usePostProjectsByProjectIdResultsDownloadcrosstabsMutation: usePostDownloadProjectCrossTabCsv,
  useGetProjectsByProjectIdSurveyQuestionsResponsetagsQuery: useGetResponseTags,
  useGetProjectsByProjectIdSurveyQuestionsQuestiongroupsQuery: useGetQuestions,
  useGetProjectsByProjectIdResultsExploreresultsQuery: useGetExploreResults,
  useGetProjectsByProjectIdSurveyGroupsEnabledQuery: useGetEnabledSurveyGroups,
  useGetProjectsByProjectIdSurveyGroupsQuery: useGetSurveyGroupsForEdit,
  usePutProjectsByProjectIdSurveyGroupsMutation: useSaveSurveyGroups,
  usePatchProjectsByProjectIdSurveyGroupsAndSurveyGroupIdColorMutation: useSetSurveyGroupColor,
  useGetProjectsByProjectIdSurveyGroupsProjectIndexLegendQuery,
  useGetProjectsByProjectIdResultsAlienationanalysisQuery: useGetAlienationAnalysisQuery,
  useGetProjectsByProjectIdResultsPenaltyanalysisQuery: useGetPenaltyAnalysis,
  useGetProjectsByProjectIdSegmentsAllowedopsQuery: useGetSegmentAllowedOps,
  useGetProjectsByProjectIdSegmentsQuery: useGetSegments,
  useGetProjectsByProjectIdSegmentsEnabledQuery: useGetEnabledSegments,
  usePutProjectsByProjectIdSegmentsUpsertMutation: useUpsertSegmentsMutation,
  useDeleteProjectsByProjectIdSegmentsAndIdMutation: useDeleteSegmentsMutation,
  useGetProjectsByProjectIdSurveyGroupsProductGroupsQuery: useGetProductGroupsForEdit,
  usePutProjectsByProjectIdSurveyGroupsProductGroupsMutation: useUpsertProductGroupsMutation,
  useDeleteProjectsByProjectIdSurveyGroupsProductGroupsAndProductGroupIdMutation:
    useDeleteProductGroupsMutation,
  useGetSurveyConfigsQuery,
  useLazyGetProjectsByProjectIdSurveyConfigsQuery: useLazyGetSurveyConfigsByProjectIdQuery,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdMaterializeMutation:
    useMaterializeSurveyConfigByIdMutation,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdPreviewMutation:
    usePreviewSurveyConfigByIdMutation,
  usePutProjectsByProjectIdSurveyConfigsAndSurveyConfigIdMutation: useEditSurveyConfigMutation,
  useGetSurveyConfigBlockGroupsQuery,
  useDeleteSurveyConfigsBySurveyConfigIdSurveyConfigBlockGroupsAndSurveyConfigBlockGroupIdMutation,

  useGetProjectsByProjectIdResultsmetaQuery: useGetResultsMeta,
  usePutProjectsByProjectIdResultsmetaMutation: useSaveResultsMeta,
  useGetProjectsByProjectIdSegmentsAndSegmentIdCountsQuery: useGetSegmentCounts,
  usePostProjectsByProjectIdResultsGetsummaryMutation: useGetSummary,
  usePostProjectsByProjectIdSurveyconfigsAndSurveyConfigIdResultContextsMutation,
  useGetAggregationtypesQuery,
  useGetProjectsByProjectIdResultsGetcrosstabdataQuery: useGetCrosstabData,
  usePostShipmentsMutation: useCreateShipmentsMutation,
  usePutShipmentsMutation: useUpdateShipmentsMutation,
  usePostProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesV2Mutation:
    useAssignSlotValuesMutationV2,
  usePostProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesBulkAssignMutation:
    useBulkAssignSlotValuesMutation,
  useDeleteProjectsByProjectIdSurveyConfigBlocksAndSurveyConfigBlockIdSlotValuesSlotValueIdMutation:
    useDeleteSlotValueMutation,
  useGetProjectsByProjectIdSolutionsQuery: useGetSolutionByProjectIdQuery,
  useGetSolutionsQuery,
  useGetSolutionsBySolutionIdSurveyDesignsQuery: useGetSurveyDesignsBySolutionIdQuery,
  useLazyGetSolutionsBySolutionIdSurveyDesignsQuery: useLazyGetSurveyDesignsBySolutionIdQuery,
  usePostSolutionsBySolutionIdSurveyDesignsAssignMutation: usePostSurveyDesignsBySolutionIdMutation,
  usePatchSolutionsBySolutionIdMutation: useUpdateSolutionMutation,
  usePostSolutionsMutation: useCreateSolutionMutation,
  useGetProjectsByProjectIdSurveyBundlesQuery: useGetSurveyBundlesQuery,
  useLazyGetProjectsByProjectIdSurveyBundlesQuery: useLazyGetSurveyBundlesQuery,
  usePostProjectsByProjectIdSurveyBundlesMutation: useCreateSurveyBundleMutation,
  usePutProjectsByProjectIdSurveyBundlesAndSurveyBundleIdMutation: useUpdateSurveyBundleMutation,
  useGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksQuery: useGetSurveyConfigBlocksQuery,
  useLazyGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksQuery:
    useLazyGetSurveyConfigBlocksQuery,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksAssignMutation:
    useAssignSurveyConfigBlocksMutation,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksSurveyConfigBlockIdPreviewMutation:
    usePreviewSurveyConfigBlockByIdMutation,
  usePutProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksSurveyConfigBlockIdMutation:
    useUpdateSurveyConfigBlockMutation,
  usePutProjectsByProjectIdSurveyConfigsAndSurveyConfigIdBlocksMutation:
    useUpdateSurveyConfigBlocksMutation,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdChoicesSurveyConfigQuestionChoiceIdMutation:
    useUpdateSurveyConfigChoiceMutation,
  useGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdRoutingsQuery:
    useGetSurveyConfigRoutingsQuery,
  useLazyGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdRoutingsQuery:
    useLazyGetSurveyConfigRoutingsQuery,
  useGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdRoutingsQuery:
    useGetSurveyConfigQuestionRoutingsQuery,
  useLazyGetProjectsByProjectIdSurveyConfigsAndSurveyConfigIdQuestionsSurveyConfigQuestionIdRoutingsQuery:
    useLazyGetSurveyConfigQuestionRoutingsQuery,

  useGetProjectsByProjectIdSurveyConfigsQuery: useGetSurveyConfigsByProjectIdQuery,
  usePutProjectsByProjectIdSurveyConfigsAndSurveyConfigIdMutation:
    useUpdateProjectSurveyConfigMutation,
  usePostProjectsByProjectIdSurveyConfigsAndSurveyConfigIdMaterializeMutation:
    useMaterializeProjectSurveyConfigMutation,

  useGetProjectsByProjectIdSurveyQuestionsProfilequestionsQuery: useGetProfileQuestionsQuery,

  useGetTestCategoriesQuery,
  usePostTeamsMutation: useCreateTeamMutation,
  useGetTeamsByTeamIdQuery: useGetTeamByIdQuery,
  usePostProjectsByProjectIdSurveyConfigsCopySurveyContentMutation:
    useCopySurveyConfigContentMutation,
  useLazyGetProjectsByProjectIdPackingListBoxConfigurationsQuery: useLazyDownloadPackingListCsv,
  useLazyGetProjectsByProjectIdPackingListSimplifiedBoxListQuery: useLazyDownloadBoxListCsv,
  usePostProjectsByProjectIdSegmentsBackfillDefaultSegmentsMutation:
    useBackfillDefaultSegmentsMutation,
  useGetProjectsByProjectIdResultsCustomTopicsQuery: useGetCustomTopicsQuery,
  usePostProjectsByProjectIdResultsCustomTopicsMutation: useAddCustomTopicMutation,
  useDeleteProjectsByProjectIdResultsCustomTopicsAndIdMutation: useDeleteCustomTopicMutation,
} = projectApi;

// Shipment reads, deletes and receiving go to each app's own Node routes under /api; projectApi's
// base is the ProjectApi proxy, so these call /api themselves, sharing its cache and tags.
const nodeBaseQuery = fetchBaseQuery({ baseUrl: '/api/' });

async function getNodeJson<T>(
  args: string | FetchArgs,
  api: Parameters<typeof nodeBaseQuery>[1],
  extraOptions: Parameters<typeof nodeBaseQuery>[2],
) {
  const result = await nodeBaseQuery(args, api, extraOptions);
  return result.error ? { error: result.error } : { data: result.data as T };
}

export const shipmentsApi = projectApi.injectEndpoints({
  // Hot reload runs this again; without it RTK keeps the first definition.
  overrideExisting: true,
  endpoints: (build) => ({
    getProjectShipments: build.query<SavedShipment[], { projectId: number }>({
      queryFn: ({ projectId }, api, extraOptions) =>
        getNodeJson<SavedShipment[]>(`projects/${projectId}/shipments`, api, extraOptions),
      providesTags: (shipments) => [
        ...(shipments ?? []).map(({ id }) => ({ type: TAG_TYPES.SHIPMENT, id })),
        TAG_TYPES.SHIPMENT,
      ],
    }),
    getShipmentDeliveryReceipts: build.query<
      SavedDeliveryReceipt[],
      { projectId: number; shipmentId: number }
    >({
      queryFn: ({ projectId, shipmentId }, api, extraOptions) =>
        getNodeJson<SavedDeliveryReceipt[]>(
          `projects/${projectId}/shipments/${shipmentId}/delivery-receipts`,
          api,
          extraOptions,
        ),
      providesTags: [TAG_TYPES.SHIPMENT],
    }),
    deleteShipment: build.mutation<null, { shipment: Types.Shipment }>({
      queryFn: async ({ shipment }, api, extraOptions) => {
        if (shipment.id == null || shipment.projectId == null) {
          return {
            error: { status: 'CUSTOM_ERROR', error: 'Missing project or shipment id' } as const,
          };
        }
        const result = await nodeBaseQuery(
          { url: `projects/${shipment.projectId}/shipments/${shipment.id}`, method: 'DELETE' },
          api,
          extraOptions,
        );
        return result.error ? { error: result.error } : { data: null };
      },
      // Patched rather than invalidated: a refetch reads the replica, which may still hold the row.
      async onQueryStarted({ shipment }, { dispatch, queryFulfilled }) {
        const { id, projectId } = shipment;
        if (id == null || projectId == null) return;
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          shipmentsApi.util.updateQueryData('getProjectShipments', { projectId }, (shipments) =>
            shipments.filter((saved) => saved.id !== id),
          ),
        );
      },
    }),
    confirmShipmentReceiving: build.mutation<
      ConfirmedDelivery,
      { projectId: number; shipmentId: number; confirmation: DeliveryConfirmationRequest }
    >({
      queryFn: async ({ projectId, shipmentId, confirmation }, api, extraOptions) => {
        const result = await nodeBaseQuery(
          {
            url: `projects/${projectId}/shipments/${shipmentId}/delivery-confirmation`,
            method: 'POST',
            body: confirmation,
          },
          api,
          extraOptions,
        );
        return result.error ? { error: result.error } : { data: result.data as ConfirmedDelivery };
      },
      // Patched from the writer's answer rather than invalidated: a refetch reads the replica.
      async onQueryStarted({ projectId, shipmentId }, { dispatch, queryFulfilled }) {
        const confirmed = await queryFulfilled.then(({ data }) => data).catch(() => null);
        if (!confirmed) return;
        dispatch(
          shipmentsApi.util.updateQueryData(
            'getShipmentDeliveryReceipts',
            { projectId, shipmentId },
            () => confirmed.receipts,
          ),
        );
        dispatch(
          shipmentsApi.util.updateQueryData('getProjectShipments', { projectId }, (shipments) =>
            shipments.map((shipment) =>
              shipment.id === shipmentId ? confirmed.shipment : shipment,
            ),
          ),
        );
      },
    }),
  }),
});

export const { useGetProjectShipmentsQuery, useLazyGetProjectShipmentsQuery } = shipmentsApi;

export const recruitCountApi = projectApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    postProjectRecruitCount: build.mutation<
      number,
      { projectId: number; body: RecruitCountRequest }
    >({
      queryFn: ({ projectId, body }, api, extraOptions) =>
        getNodeJson<number>(
          { url: `projects/${projectId}/recruit-count`, method: 'POST', body },
          api,
          extraOptions,
        ),
    }),
  }),
});

export const { usePostProjectRecruitCountMutation } = recruitCountApi;
